<template>
  <div
    :class="[
      'v-layout',
      vuepress && 'v-layout--vuepress',
      { 'v-layout--hideNavbar': hideNavbar }
    ]"
    :style="mobile && alerts && !vuepress && {paddingTop: alertsPaddingTop()}"
  >
    <div
      id="jumper"
      tabindex="0"
      class="v-layout__contentJump"
      @click="jumpTo()"
      @keydown.enter="jumpTo()"
      v-if="!vuepress"
    >
      <v-text class="v-layout__contentJump__clickable" tag="a">{{ $t("Go to content", "components.jumpToContent.goTo") }}</v-text>
      <br/>
      <v-text font-size="1">{{ $t("Press enter", "components.jumpToContent.pressEnter") }}</v-text>
    </div>
    <!-- Hidden portal target used to measure height of alerts,
    which is needed to adjust v-layout top padding on mobile-->
    <div class="v-layout__hidden-alerts" v-if="!vuepress">
      <portal-target @change="onAlerts" name="alerts" ref="hiddenAlerts" multiple slim />
    </div>

    <!-- Navigation with optional alerts-->
    <v-navbar :alerts="alerts" :alerts-height="alertsHeight" :mobile="mobile" v-if="displayNav">
      <template #alerts>
        <portal-target name="alerts" multiple slim />
      </template>
      <slot name="navigation"></slot>
    </v-navbar>

    <!-- Default content slot -->
    <slot></slot>

    <!-- Toasts -->
    <v-toast
      v-for="(toast, index) in toasts"
      v-bind:key="index"
      :success="toast.type === 'success'"
      :info="toast.type === 'info'"
      :error="toast.type === 'error'"
      :dismissible="toast.dismissible"
    >{{toast.message}}</v-toast>
    <div class="app notifications">
      <portal-target name="notifications" multiple />
    </div>

    <!-- Modals -->
    <div v-if="!vuepress" class="app modals">
      <portal-target name="modals" multiple />
    </div>

    <!-- Search -->
    <div v-if="!vuepress" class="app search">
      <portal-target name="search" multiple />
    </div>

    <!-- Widget -->
    <div v-if="!vuepress" class="app widget">
      <portal-target name="widget" />
    </div>
  </div>
</template>

<script>
import vText from "../v-text/v-text.vue";
export default {
  components: { vText },
  props: {
    vuepress: {
      type: Boolean,
      default: false
    },
    hideNavbar: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      toasts: [],
      mobile: false,
      alerts: false,
      alertsHeight: 0
    };
  },
  created() {
    if (
      typeof window !== "undefined" &&
      !window.matchMedia("(min-width: 768px)").matches
    ) {
      this.mobile = true;
    }
  },
  mounted() {
    this.$root.$on("toast", this.onToast);
    this.enforceHashNavigation();
  },
  beforeDestroy() {
    this.$root.$off("toast", this.onToast);
  },
  methods: {
    jumpTo() {
      const el = document.getElementById("jumpToContent");
      const top = el.offsetTop - 100;

      window.scrollTo(0, top);
      el.focus();
    },
    enforceHashNavigation() {
      if (typeof location !== "undefined" && location.hash) {
        this.$nextTick(() => {
          location.href = location.href;
        });
      }
    },
    onAlerts(hasContent) {
      if (!hasContent) return;

      if (this.mobile) {
        this.$nextTick(() => this.adjustMobileAlertsSize());
      }

      this.alerts = true;
    },
    onToast(toast) {
      this.toasts.push(toast);
    },
    adjustMobileAlertsSize() {
      if (!this.alertsCollapsed()) {
        this.setHiddenAlertsWidth();
        this.findAlertsHeight();
      }
    },
    setHiddenAlertsWidth() {
      this.hiddenAlerts.style.width = `${this.documentWidth()}px`;
    },
    findAlertsHeight() {
      let heights = [];

      this.hiddenAlerts
        .querySelectorAll(".v-alerts__content > .v-text")
        .forEach(alertEl => heights.push(alertEl.scrollHeight));

      this.alertsHeight = Math.max(...heights) + 24; //24 = padding
    },
    alertsCollapsed() {
      return this.hiddenAlerts.scrollHeight == 0;
    },
    alertsPaddingTop() {
      return `${this.alertsHeight + 60}px`;
    },
    documentWidth() {
      return Math.max(document.documentElement.clientWidth, window.innerWidth);
    }
  },
  computed: {
    hiddenAlerts() {
      return this.$refs.hiddenAlerts.$el;
    },
    displayNav() {
      if (this.vuepress || this.hideNavbar) {
        return false;
      }
      return true;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

.v-layout {
  padding-top: $nav-height-mobile;

  @include media-breakpoint-up(md) {
    padding-top: $nav-height-desktop;
  }

  @media print {
    padding-top: 0;
  }

  &__contentJump {
    border: 2px black solid;
    border-radius: 5px;
    cursor: pointer;
    text-align: center;
    padding: 10px;
    position: absolute;
    top: -100px;
    left: 15%;
    margin-top: 5px;
    background-color: theme-color("white");
    transform: translate(-50%, calc(-100% - 12px));
    z-index: 10000;
    transition: 0.3s ease-in-out all;

    &:hover {
      .v-layout__contentJump__clickable {
        text-decoration: underline;
      }
    }

    &:focus {
      top: 0;
      transform: translate(-50%, 0%);
    }
  }

  &--vuepress {
    padding-top: 0;
  }

  &--hideNavbar {
    padding-top: 0;
  }

  &__hidden-alerts {
    position: absolute;
    margin-left: -10000px;
    text-size-adjust: none;

    >>> .v-alerts__content > .v-text {
      display: block !important;
    }
  }
}

.notifications {
  position: fixed;
  z-index: 2000;
  padding: 0 $spacer;
  right: 0;
  bottom: 0;

  >>> .vue-portal-target {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-end;
  }
}
</style>
